import backIcon from '@iconify/icons-material-symbols/arrow-back-rounded';
import closeIcon from '@iconify/icons-material-symbols/close-rounded';
import historyIcon from '@iconify/icons-material-symbols/history-rounded';
import sendIcon from '@iconify/icons-material-symbols/send-rounded';
import { Icon } from '@iconify/react';
import { LoadingButton } from '@mui/lab';
import { visuallyHidden } from '@mui/utils';
import {
    Box,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    FormControlLabel,
    IconButton,
    Link,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { streamChatMessage } from 'external/firebase/controllers/chat';
import useAuth from 'hooks/useAuth';
import useLocales from 'hooks/useLocales';

import ChatSessionList from './ChatSessionList';
import {
    capSessions,
    ChatSession,
    hasGivenChatConsent,
    loadStore,
    recordChatConsent,
    saveStore,
    StoredMessage,
    upsertMessage,
} from './chatSessionStore';

// ----------------------------------------------------------------------

// Legacy flat key from before per-session persistence. It only ever held a bare
// session_id (no transcript), so there's nothing to migrate — we drop it on load.
const LEGACY_SESSION_KEY = 'kursuok_chat_session_id';

// Drag-to-resize width, remembered across sessions (not per-uid — it's a
// viewport preference, not user data). Only meaningful at the `sm` breakpoint
// and up; below that the drawer is always full-width (see the Drawer's
// PaperProps) and there's nothing to resize.
const CHAT_WIDTH_KEY = 'kursuok_chat_width';
const DEFAULT_CHAT_WIDTH = 420;
const MIN_CHAT_WIDTH = 320;
// Keep at least this much of the page visible on the left rather than letting
// the drawer swallow the whole viewport.
const MIN_VISIBLE_PAGE_WIDTH = 480;

const clampChatWidth = (width: number): number => {
    const max = Math.max(MIN_CHAT_WIDTH, window.innerWidth - MIN_VISIBLE_PAGE_WIDTH);
    return Math.min(Math.max(width, MIN_CHAT_WIDTH), max);
};

const loadStoredChatWidth = (): number => {
    try {
        const raw = localStorage.getItem(CHAT_WIDTH_KEY);
        const parsed = raw ? Number(raw) : NaN;
        return Number.isFinite(parsed) ? clampChatWidth(parsed) : DEFAULT_CHAT_WIDTH;
    } catch {
        return DEFAULT_CHAT_WIDTH;
    }
};

interface ChatPanelProps {
    open: boolean;
    onClose: () => void;
}

// crypto.randomUUID needs a secure context, which every real deployment of
// this app is; the fallback just keeps plain-http local dev working too.
const generateSessionId = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

// ----------------------------------------------------------------------

const LINK_OR_BOLD = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*/g;

// Minimal renderer for the markdown the scraper emits: [label](url) links and
// **bold**. Anything else is shown verbatim, with newlines preserved. Built from
// React nodes (no dangerouslySetInnerHTML) so untrusted model output stays inert.
const renderRichText = (text: string): React.ReactNode => {
    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let key = 0;

    const pushPlain = (chunk: string) => {
        const lines = chunk.split('\n');
        lines.forEach((line, i) => {
            if (line) {
                nodes.push(<React.Fragment key={`t${key++}`}>{line}</React.Fragment>);
            }
            if (i < lines.length - 1) {
                nodes.push(<br key={`b${key++}`} />);
            }
        });
    };

    let match = LINK_OR_BOLD.exec(text);
    while (match !== null) {
        pushPlain(text.slice(lastIndex, match.index));
        if (match[1] && match[2]) {
            nodes.push(
                <Link key={`l${key++}`} href={match[2]} target='_blank' rel='noopener noreferrer'>
                    {match[1]}
                </Link>,
            );
        } else if (match[3]) {
            nodes.push(<strong key={`s${key++}`}>{match[3]}</strong>);
        }
        lastIndex = match.index + match[0].length;
        match = LINK_OR_BOLD.exec(text);
    }
    pushPlain(text.slice(lastIndex));
    LINK_OR_BOLD.lastIndex = 0;
    return nodes;
};

// ----------------------------------------------------------------------

const TYPEWRITER_TICK_MS = 20;

// The reveal speeds up the further it falls behind what's already buffered
// (e.g. once the whole answer has arrived while typing is still catching
// up), so a long reply doesn't drag on forever once there's nothing left to
// actually wait for.
const typewriterStepSize = (backlog: number): number => {
    if (backlog > 200) {
        return 10;
    }
    if (backlog > 60) {
        return 4;
    }
    return 1;
};

interface TypewriterHandle {
    // Resolves once `isStreamDone()` is true AND every buffered character has
    // been shown — never earlier, so the caller can't finalize the message
    // mid-animation.
    done: Promise<void>;
    // Stops the reveal immediately, e.g. because the turn failed and the
    // partial text is about to be discarded rather than finished typing out.
    stop: () => void;
}

// Reveals `getTarget()`'s current text a few characters at a time via
// `onUpdate`, on its own steady clock — independent of how the SSE tokens
// actually arrived. Raw token timing makes a poor animation on its own: a
// chunk can be a single word or three whole sentences, so displaying each
// chunk the instant it lands would jump in uneven bursts instead of reading
// as one smooth "self-writing" reply.
const revealTextOverTime = (
    getTarget: () => string,
    isStreamDone: () => boolean,
    onUpdate: (shown: string) => void,
    isUnmounted: () => boolean,
): TypewriterHandle => {
    let shown = '';
    let timer: ReturnType<typeof setInterval> | null = null;
    const stop = (): void => {
        if (timer !== null) {
            clearInterval(timer);
            timer = null;
        }
    };
    const done = new Promise<void>(resolve => {
        const tick = (): void => {
            const target = getTarget();
            const backlog = target.length - shown.length;
            if (backlog > 0) {
                shown = target.slice(0, shown.length + typewriterStepSize(backlog));
                if (!isUnmounted()) {
                    onUpdate(shown);
                }
            }
            if (backlog <= 0 && isStreamDone()) {
                stop();
                resolve();
            }
        };
        timer = setInterval(tick, TYPEWRITER_TICK_MS);
    });
    return { done, stop };
};

// ----------------------------------------------------------------------

const ChatPanel: React.FunctionComponent<ChatPanelProps> = ({ open, onClose }) => {
    const { translate } = useLocales();
    const { enqueueSnackbar } = useSnackbar();
    const { user, isInitialized } = useAuth();
    const uid = user?.uid;

    const [sessions, setSessions] = useState<ChatSession[]>([]);
    const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    // The in-flight assistant reply, growing token by token as it streams in —
    // the "self-writing" animation. null while nothing is streaming; '' the
    // instant a turn starts (before the first token lands), which is what
    // keeps the "thinking" indicator up until there's real text to show.
    // Never touches `sessions`/localStorage — only a complete turn does, on
    // the stream's `done` event, same "no half-turns persisted" rule the old
    // buffered flow already followed.
    const [streamingText, setStreamingText] = useState<string | null>(null);
    const [showSessions, setShowSessions] = useState(false);
    // Shown before every new chat is minted — see handleRequestNewChat below.
    const [showIntro, setShowIntro] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
    const [width, setWidth] = useState(loadStoredChatWidth);
    // Recorded once per uid, closed by default — see chatSessionStore's
    // hasGivenChatConsent/recordChatConsent and the intro screen below.
    const [hasConsented, setHasConsented] = useState(false);
    // The current intro screen's checkbox state — only meaningful while
    // !hasConsented; reset each time the intro is (re)shown.
    const [consentChecked, setConsentChecked] = useState(false);

    const loadedUidRef = useRef<string | null>(null);
    const primedRef = useRef(false);
    const sendingRef = useRef(false);
    const creatingRef = useRef(false);
    const resizingRef = useRef(false);
    // The scrollable message-list Box (overflowY: 'auto', set on the element
    // below) — see the scroll-to-bottom effect for why its scrollTop is set
    // directly rather than via an anchor element's .scrollIntoView().
    const messagesContainerRef = useRef<HTMLDivElement | null>(null);
    // The in-flight letter-by-letter typewriter — see revealTextOverTime below.
    // Kept at component scope purely so unmount can stop it; only one send is
    // ever in flight at a time (sendingRef guards that), so a single ref is enough.
    const typewriterRef = useRef<TypewriterHandle | null>(null);
    const unmountedRef = useRef(false);

    useEffect(
        () => () => {
            unmountedRef.current = true;
            typewriterRef.current?.stop();
        },
        [],
    );

    const theme = useTheme();
    // The drawer is always full-width below `sm` (see the Drawer's PaperProps
    // below) — nothing to resize there, so the handle isn't rendered at all.
    const isResizable = useMediaQuery(theme.breakpoints.up('sm'));

    // Single source of truth: the active session's transcript is derived, never
    // duplicated into its own state (which would drift from what we persist).
    const messages = useMemo(
        () => sessions.find(session => session.id === activeSessionId)?.messages ?? [],
        [sessions, activeSessionId],
    );

    // Durable write: update state and persist, but only under the uid the state
    // currently reflects — so an in-flight user switch can't save A's data as B's.
    const persist = (nextSessions: ChatSession[], nextActive: string | null): void => {
        const capped = capSessions(nextSessions, nextActive);
        setSessions(capped);
        setActiveSessionId(nextActive);
        if (uid && loadedUidRef.current === uid) {
            saveStore(uid, { activeSessionId: nextActive, sessions: capped });
        }
    };

    // Hydrate from storage on mount and whenever the user changes. A session with
    // messages is restored with zero network — this is the "survives refresh" fix.
    useEffect(() => {
        if (!isInitialized) {
            return;
        }
        if (!uid) {
            loadedUidRef.current = null;
            setSessions([]);
            setActiveSessionId(null);
            primedRef.current = false;
            setHasConsented(false);
            return;
        }
        if (loadedUidRef.current === uid) {
            return;
        }
        const store = loadStore(uid);
        loadedUidRef.current = uid;
        setSessions(store.sessions);
        setActiveSessionId(store.activeSessionId);
        // Only a genuinely empty store should auto-create a session on open.
        primedRef.current = store.sessions.length > 0;
        setHasConsented(hasGivenChatConsent(uid));
        try {
            localStorage.removeItem(LEGACY_SESSION_KEY);
        } catch {
            // ignore — storage disabled
        }
    }, [uid, isInitialized]);

    // Mint a fresh local chat session with a canned opening greeting — no network
    // call. This used to prime the session with a throwaway "Sveiki" message sent
    // to the backend, a holdover from the old scraper bot, which used to discard
    // a session's first message and reply with its own greeting instead. Our own
    // /v1/chat has no such discard behavior, so priming only spent a real LLM call
    // — and worse, "Sveiki" itself would retrieve unrelated course content and cite
    // it in a "Sources" footer under a message that was never actually answering
    // anything. Only ever called after the intro step's "Continue" — see
    // handleContinueFromIntro. The session_id is generated client-side;
    // cao-content-service creates the matching conversation row lazily on the
    // first real message (see chat.py's get_or_create_conversation).
    const createSession = (): void => {
        if (creatingRef.current) {
            return;
        }
        creatingRef.current = true;
        const now = Date.now();
        const session: ChatSession = {
            id: generateSessionId(),
            title: '',
            createdAt: now,
            updatedAt: now,
            messages: [{ role: 'assistant', content: translate('chat_greeting') }],
        };
        persist([session, ...sessions], session.id);
        creatingRef.current = false;
    };

    // Every path that starts a new chat lands here first — the explicit "New chat"
    // button and the first-ever-open auto-prompt below — rather than minting a
    // session straight away. See handleContinueFromIntro for the actual creation.
    const handleRequestNewChat = (): void => {
        setShowSessions(false);
        setConsentChecked(false);
        setShowIntro(true);
    };

    const handleContinueFromIntro = (): void => {
        if (!hasConsented) {
            if (!consentChecked) {
                return;
            }
            if (uid) {
                recordChatConsent(uid);
            }
            setHasConsented(true);
        }
        setShowIntro(false);
        createSession();
    };

    // Only meaningful once there's an existing conversation to return to — the
    // very first ever open has nowhere to go back to but closing the drawer.
    const handleCancelIntro = (): void => {
        setShowIntro(false);
        if (sessions.length > 0) {
            setShowSessions(true);
        }
    };

    // Prompt for the first session the first time the panel opens with an empty
    // store. Keyed on sessions.length so it re-reads state rather than closing over
    // a stale snapshot.
    useEffect(() => {
        if (!open || !isInitialized || !uid || loadedUidRef.current !== uid) {
            return;
        }
        if (sessions.length > 0 || primedRef.current || creatingRef.current) {
            return;
        }
        primedRef.current = true;
        setConsentChecked(false);
        setShowIntro(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isInitialized, uid, sessions.length]);

    // Sets scrollTop directly on the message-list container rather than
    // calling `.scrollIntoView()` on an anchor element. scrollIntoView walks
    // up and scrolls EVERY scrollable ancestor needed to bring the target
    // into view, not just the intended inner container — if any ancestor
    // between the anchor and the message-list Box isn't exactly as tall as
    // expected for a render or two (entirely possible right as the drawer's
    // content mounts, or as the intro/session-list/message-view bodyContent
    // branch swaps), it can end up scrolling the Drawer's own Paper (or, in
    // the worst case, the page behind it) instead of — or in addition to —
    // the message list, which is what produced the "everything hops to the
    // top" glitch. Writing `scrollTop` on a specific element can't escape to
    // an ancestor: there's nothing to accidentally get right or wrong about
    // which container ends up moving. Instant, not smooth-animated, for the
    // same reason as before — streamingText changes on every ~20ms
    // typewriter tick (see revealTextOverTime), and restarting a smoothed
    // scroll animation that often fights itself is what read as jittery
    // "hopping" during a reply. `open` is in the deps so reopening the
    // drawer on an existing conversation lands at the bottom (the latest
    // messages), not wherever the fresh DOM node defaults to (the top) —
    // MUI's Drawer unmounts its content while closed, so the scroll position
    // from before closing isn't there to restore.
    useEffect(() => {
        if (!open) {
            return;
        }
        const container = messagesContainerRef.current;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    }, [open, messages, loading, streamingText]);

    const handleSend = async () => {
        const text = input.trim();
        // Guard synchronously (a ref, not `loading` state) so two Enter presses
        // in the same frame can't both slip through and fire duplicate sends.
        if (!text || sendingRef.current || !activeSessionId) {
            return;
        }
        sendingRef.current = true;

        const sid = activeSessionId;
        const base = sessions;
        const userMessage: StoredMessage = { role: 'user', content: text };
        setInput('');
        // Optimistic: show the user bubble immediately, but don't persist a
        // half-turn — only complete turns reach localStorage (see the success path).
        setSessions(upsertMessage(base, sid, userMessage, text));
        setLoading(true);
        setStreamingText('');

        // `assembled` is the full text as it actually arrives over the network;
        // `streamingText` (rendered) instead follows revealTextOverTime's own
        // steady clock — see that function for why the two are kept separate.
        let assembled = '';
        let networkDone = false;
        const typewriter = revealTextOverTime(
            () => assembled,
            () => networkDone,
            setStreamingText,
            () => unmountedRef.current,
        );
        typewriterRef.current = typewriter;
        const res = await streamChatMessage(text, sid, delta => {
            assembled += delta;
        });
        networkDone = true;

        if ('session_id' in res) {
            // Let the animation actually finish catching up before the bubble
            // becomes a persisted, static message — otherwise the reply would
            // snap to its full text mid-typewriter.
            await typewriter.done;
            typewriterRef.current = null;
            setStreamingText(null);
            const withUser = upsertMessage(base, sid, userMessage, text);
            const withReply = upsertMessage(withUser, sid, { role: 'assistant', content: assembled });
            persist(withReply, sid);
        } else {
            // The turn failed — stop the animation immediately rather than
            // typing out text that's about to be discarded anyway.
            typewriter.stop();
            typewriterRef.current = null;
            setStreamingText(null);
            // Undo the failed turn so the user can retry without retyping: drop
            // the orphaned user bubble and put the text back in the input.
            setInput(text);
            let errorKey:
                | 'forbidden_access'
                | 'chat_rate_limited'
                | 'chat_session_conflict'
                | 'chat_not_in_pilot'
                | 'something_went_wrong' = 'something_went_wrong';
            if (res.code === 'auth/forbidden-access') {
                errorKey = 'forbidden_access';
                setSessions(base);
            } else if (res.code === 'chat/rate-limited') {
                errorKey = 'chat_rate_limited';
                setSessions(base);
            } else if (res.code === 'chat/not-in-pilot') {
                errorKey = 'chat_not_in_pilot';
                setSessions(base);
            } else if (res.code === 'chat/session-conflict') {
                // This session's id collided with a conversation owned by a
                // different uid server-side (cao-content-service's
                // ConversationOwnershipError — vanishingly rare, but the old
                // id can never work again). Give the session a fresh one so
                // retrying with the restored text actually succeeds.
                errorKey = 'chat_session_conflict';
                const freshId = generateSessionId();
                const withFreshId = base.map(session => (session.id === sid ? { ...session, id: freshId } : session));
                persist(withFreshId, freshId);
            } else {
                setSessions(base);
            }
            enqueueSnackbar(translate(errorKey), { variant: 'error' });
        }
        setLoading(false);
        sendingRef.current = false;
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    const handleSelectSession = (id: string) => {
        persist(sessions, id);
        setShowSessions(false);
    };

    const handleConfirmDelete = () => {
        if (!deleteTargetId) {
            return;
        }
        const next = sessions.filter(session => session.id !== deleteTargetId);
        const nextActive =
            activeSessionId === deleteTargetId
                ? [...next].sort((a, b) => b.updatedAt - a.updatedAt)[0]?.id ?? null
                : activeSessionId;
        persist(next, nextActive);
        if (next.length === 0) {
            // Empty again — let the primer effect re-create a session on reopen.
            primedRef.current = false;
        }
        setDeleteTargetId(null);
    };

    // Pointer capture (not window listeners) keeps every subsequent move/up
    // event routed to this element even once the pointer leaves it — the
    // standard way to implement a drag handle without a useEffect + manual
    // window-listener cleanup dance.
    const handleResizeStart = (event: React.PointerEvent<HTMLDivElement>): void => {
        event.preventDefault();
        resizingRef.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handleResizeMove = (event: React.PointerEvent<HTMLDivElement>): void => {
        if (!resizingRef.current) {
            return;
        }
        // Anchored right: dragging the left edge further left grows the panel.
        setWidth(clampChatWidth(window.innerWidth - event.clientX));
    };

    const handleResizeEnd = (event: React.PointerEvent<HTMLDivElement>): void => {
        if (!resizingRef.current) {
            return;
        }
        resizingRef.current = false;
        event.currentTarget.releasePointerCapture(event.pointerId);
        try {
            localStorage.setItem(CHAT_WIDTH_KEY, String(width));
        } catch {
            // ignore — storage disabled
        }
    };

    let headerTitle = translate('chat_title');
    if (showIntro) {
        headerTitle = translate('chat_intro_header');
    } else if (showSessions) {
        headerTitle = translate('chat_history');
    }

    let bodyContent: React.ReactNode;
    if (showIntro) {
        bodyContent = (
            <Box
                sx={{
                    flexGrow: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    px: 3,
                    py: 3,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Stack spacing={2} sx={{ flexGrow: 1 }}>
                    <Typography variant='h6'>{translate('chat_intro_title')}</Typography>
                    <Typography variant='body2' color='text.secondary'>
                        {translate('chat_intro_body_1')}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        {translate('chat_intro_body_2')}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        {translate('chat_intro_body_3')}
                    </Typography>
                </Stack>
                {!hasConsented && (
                    <FormControlLabel
                        sx={{ mt: 2, alignItems: 'flex-start' }}
                        control={
                            <Checkbox
                                checked={consentChecked}
                                onChange={event => setConsentChecked(event.target.checked)}
                                sx={{ pt: 0 }}
                            />
                        }
                        label={
                            <Typography variant='body2' color='text.secondary'>
                                {translate('chat_consent_label')}
                            </Typography>
                        }
                    />
                )}
                <LoadingButton
                    fullWidth
                    variant='contained'
                    loading={loading}
                    disabled={!hasConsented && !consentChecked}
                    onClick={handleContinueFromIntro}
                    sx={{ mt: hasConsented ? 2 : 1 }}
                >
                    {translate('chat_intro_continue')}
                </LoadingButton>
            </Box>
        );
    } else if (showSessions) {
        bodyContent = (
            <ChatSessionList
                sessions={sessions}
                activeSessionId={activeSessionId}
                busy={loading}
                onSelect={handleSelectSession}
                onNew={handleRequestNewChat}
                onRequestDelete={setDeleteTargetId}
            />
        );
    } else {
        bodyContent = (
            <Box ref={messagesContainerRef} sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 2 }}>
                <Stack spacing={1.5}>
                    {messages.map((message, index) => (
                        <Box
                            key={index}
                            sx={{
                                alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                                maxWidth: '85%',
                                px: 1.5,
                                py: 1,
                                borderRadius: 1.5,
                                bgcolor: message.role === 'user' ? 'primary.main' : 'grey.200',
                                color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                            }}
                        >
                            <Typography variant='body2' component='div' sx={{ wordBreak: 'break-word' }}>
                                {renderRichText(message.content)}
                            </Typography>
                        </Box>
                    ))}
                    {/* Three bouncing dots, staggered by animation-delay, while waiting for the
                        first character — replaced by the self-writing bubble below the moment
                        text actually starts arriving. `role='status'` + the visually-hidden label
                        keep this announced to screen readers as "typing", not as three blank dots. */}
                    {loading && !streamingText && (
                        <Box
                            role='status'
                            sx={{
                                alignSelf: 'flex-start',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1.5,
                                py: 1.25,
                                borderRadius: 1.5,
                                bgcolor: 'grey.200',
                            }}
                        >
                            <Box component='span' sx={visuallyHidden}>
                                {translate('chat_typing')}
                            </Box>
                            {[0, 1, 2].map(dotIndex => (
                                <Box
                                    key={dotIndex}
                                    aria-hidden
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        bgcolor: 'text.secondary',
                                        '@keyframes chatTypingBounce': {
                                            '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
                                            '40%': { transform: 'scale(1)', opacity: 1 },
                                        },
                                        animation: 'chatTypingBounce 1.2s ease-in-out infinite',
                                        animationDelay: `${dotIndex * 0.15}s`,
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                    {/* The self-writing animation: revealTextOverTime paces this onto the
                        screen letter by letter on its own clock (see that function), replacing
                        the plain "typing" indicator above the moment the first characters land.
                        Not a stored message — it only becomes one once the turn completes and
                        the animation has fully caught up (see handleSend's success path). */}
                    {loading && streamingText && (
                        <Box
                            sx={{
                                alignSelf: 'flex-start',
                                maxWidth: '85%',
                                px: 1.5,
                                py: 1,
                                borderRadius: 1.5,
                                bgcolor: 'grey.200',
                                color: 'text.primary',
                            }}
                        >
                            <Typography variant='body2' component='div' sx={{ wordBreak: 'break-word' }}>
                                {renderRichText(streamingText)}
                                <Box
                                    component='span'
                                    aria-hidden
                                    sx={{
                                        display: 'inline-block',
                                        width: '2px',
                                        height: '1em',
                                        ml: '2px',
                                        verticalAlign: 'text-bottom',
                                        bgcolor: 'text.primary',
                                        '@keyframes chatCursorBlink': {
                                            '0%, 100%': { opacity: 1 },
                                            '50%': { opacity: 0 },
                                        },
                                        animation: 'chatCursorBlink 0.9s step-start infinite',
                                    }}
                                />
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </Box>
        );
    }

    return (
        <Drawer
            anchor='right'
            open={open}
            onClose={onClose}
            // PageZoom (the A+/A- accessibility widget) is fixed top-right at zIndex 1201;
            // the drawer defaults to zIndex.drawer (1200), so lift it above to avoid overlap.
            sx={{ zIndex: theme => theme.zIndex.drawer + 2 }}
            PaperProps={{
                // No explicit `position` here — MUI's own Drawer paper classes
                // already set `position: fixed` (anchoring it flush to the
                // right edge), and that's already a valid positioning context
                // for the absolutely-positioned resize handle below. Adding
                // `position: 'relative'` here previously overrode that fixed
                // positioning and made the drawer jump on open.
                sx: {
                    width: { xs: '100%', sm: `${width}px` },
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            {isResizable && (
                <Box
                    onPointerDown={handleResizeStart}
                    onPointerMove={handleResizeMove}
                    onPointerUp={handleResizeEnd}
                    // Straddles the drawer's left edge so it's easy to grab without
                    // eating into the content's own padding.
                    sx={{
                        position: 'absolute',
                        left: -3,
                        top: 0,
                        bottom: 0,
                        width: 6,
                        cursor: 'col-resize',
                        touchAction: 'none',
                        zIndex: 1,
                        '&:hover': { bgcolor: 'divider' },
                    }}
                />
            )}
            <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                sx={{ px: 2, py: 1.5, borderBottom: theme => `1px solid ${theme.palette.divider}` }}
            >
                <Typography variant='subtitle1'>{headerTitle}</Typography>
                <Stack direction='row' alignItems='center'>
                    {showIntro ? (
                        // Nothing to go back to on the very first ever open — only
                        // offer the back arrow once there's a conversation list behind it.
                        sessions.length > 0 && (
                            <IconButton
                                onClick={handleCancelIntro}
                                disabled={loading}
                                aria-label={translate('chat_back_to_conversation')}
                            >
                                <Icon icon={backIcon} width={22} height={22} />
                            </IconButton>
                        )
                    ) : (
                        <IconButton
                            onClick={() => setShowSessions(value => !value)}
                            // Disabled mid-turn so a reply can't land against a session
                            // the user has meanwhile switched away from.
                            disabled={loading}
                            aria-label={
                                showSessions ? translate('chat_back_to_conversation') : translate('chat_history')
                            }
                        >
                            <Icon icon={showSessions ? backIcon : historyIcon} width={22} height={22} />
                        </IconButton>
                    )}
                    <IconButton onClick={onClose} aria-label='close chat'>
                        <Icon icon={closeIcon} width={22} height={22} />
                    </IconButton>
                </Stack>
            </Stack>

            {bodyContent}

            {!showSessions && !showIntro && (
                <Stack
                    direction='row'
                    spacing={1}
                    alignItems='flex-end'
                    sx={{ p: 2, borderTop: theme => `1px solid ${theme.palette.divider}` }}
                >
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        size='small'
                        value={input}
                        onChange={event => setInput(event.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={translate('chat_placeholder')}
                    />
                    <LoadingButton
                        variant='contained'
                        loading={loading}
                        disabled={!input.trim() || !activeSessionId}
                        onClick={handleSend}
                        sx={{ minWidth: 0, px: 1.5 }}
                        aria-label='send message'
                    >
                        <Icon icon={sendIcon} width={22} height={22} />
                    </LoadingButton>
                </Stack>
            )}

            <Dialog open={!!deleteTargetId} onClose={() => setDeleteTargetId(null)} maxWidth='xs' fullWidth>
                <DialogTitle>{translate('chat_delete_title')}</DialogTitle>
                <DialogContent>
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                        {translate('chat_delete_body')}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
                    <LoadingButton variant='outlined' onClick={() => setDeleteTargetId(null)}>
                        {translate('cancel')}
                    </LoadingButton>
                    <LoadingButton color='error' variant='contained' onClick={handleConfirmDelete}>
                        {translate('chat_delete_confirm')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Drawer>
    );
};

export default ChatPanel;
